const API_BASE = ''

/**
 * 이미지 URL을 상대경로로 변환
 *
 * S3 업로드 URL이 WEB_URL(예: https://fiket.solboxdev.com) 기반 절대경로로 저장되는데,
 * Admin은 다른 도메인(admin.fiket.solboxdev.com)이라 CORP 차단됨.
 * `/api/uploads/...` 상대경로로 변환하면 Admin 자체 CloudFront 프록시를 타서 해결.
 */
/**
 * 이미지 URL을 상대경로로 변환 (단일 문자열)
 */
export function toLocalImageUrl(url: string): string {
  try {
    const parsed = new URL(url)
    if (parsed.pathname.startsWith('/api/uploads/')) {
      return parsed.pathname
    }
  } catch {
    // 이미 상대경로이거나 파싱 불가
  }
  return url
}

/**
 * API 응답 내 모든 /api/uploads/ URL을 상대경로로 재작성
 *
 * Admin이 Web과 다른 도메인이라 CloudFront CORP(same-origin)에 차단됨.
 * 상대경로로 바꾸면 Admin 자체 프록시를 타서 해결.
 */
function rewriteImageUrls<T>(data: T): T {
  if (data == null || typeof data !== 'object') return data
  if (Array.isArray(data)) return data.map(rewriteImageUrls) as T
  const obj = data as Record<string, unknown>
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string' && value.includes('/api/uploads/')) {
      result[key] = toLocalImageUrl(value)
    } else if (Array.isArray(value)) {
      result[key] = value.map(v =>
        typeof v === 'string' && v.includes('/api/uploads/') ? toLocalImageUrl(v) : rewriteImageUrls(v)
      )
    } else if (typeof value === 'object' && value !== null) {
      result[key] = rewriteImageUrls(value)
    } else {
      result[key] = value
    }
  }
  return result as T
}

/**
 * 현재 admin 도메인에 대응하는 web(청첩장) 도메인 origin을 반환
 *
 * 청첩장 뷰어(`/letter`, `/inv`)·공유 에셋(`/assets/...`)은 web 도메인에서만 서빙된다.
 * admin 도메인 상대경로로 열면 admin.fiket.ai 로 연결돼 깨지므로 web origin으로 치환한다.
 * dev/prod 변환 규칙이 달라(`admin.` strip vs `wedding.` 치환) 명시적으로 매핑한다.
 */
export function webOrigin(): string {
  const { origin, hostname } = window.location
  // prod
  if (hostname === 'admin.fiket.ai') return 'https://wedding.fiket.ai'
  // dev (solboxdev)
  if (hostname === 'admin.fiket.solboxdev.com') return 'https://fiket.solboxdev.com'
  // 로컬 dev: admin :5175 → web :5173
  if (hostname === 'localhost' || hostname === '127.0.0.1') return origin.replace(':5175', ':5173')
  // 알 수 없는 도메인: 선두 `admin.` 제거 폴백
  return origin.replace('//admin.', '//')
}

/** web 도메인 기준 절대 URL 생성 */
export function webUrl(path: string): string {
  return `${webOrigin()}${path.startsWith('/') ? path : `/${path}`}`
}

/** 동시 401 응답 시 refresh 요청을 한 번만 보내기 위한 공유 Promise */
let refreshPromise: Promise<boolean> | null = null

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: { code: string; message: string; details?: unknown }
}

/**
 * 인증 쿠키 포함 raw fetch — 401이면 /auth/refresh 후 1회 재시도하고 Response를 그대로 돌려준다.
 *
 * SSE 스트리밍·FormData 업로드처럼 apiFetch의 JSON 파싱을 거칠 수 없는 요청용.
 * access token 수명(15분)이 지나도 다른 API 호출이 없으면 갱신 기회가 없으므로,
 * 인증이 필요한 raw fetch는 모두 이 함수를 거쳐야 한다. (FIKET-319 계열)
 */
export async function fetchWithAuth(path: string, init: RequestInit = {}): Promise<Response> {
  const options: RequestInit = { ...init, credentials: 'include' }

  let res = await fetch(`${API_BASE}${path}`, options)

  // 401이면 쿠키 기반 토큰 갱신 시도 (동시 요청 시 1회만 호출)
  if (res.status === 401) {
    if (!refreshPromise) {
      refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
        .then((r) => r.ok)
        .finally(() => { refreshPromise = null })
    }
    const refreshed = await refreshPromise

    if (refreshed) {
      res = await fetch(`${API_BASE}${path}`, options)
    }
  }

  return res
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  if (!headers.has('Content-Type') && init?.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const res = await fetchWithAuth(path, { ...init, headers })
  return parseApiResponse<T>(res)
}

/**
 * 응답 → data 언랩. 본문 없는 성공(204 등)은 undefined, 실패는 status/code를 실은 Error로 throw.
 * `res.json()`을 바로 부르면 204(삭제·연결 해제)나 게이트웨이 HTML 오류에서 "Unexpected end of JSON input"
 * 같은 파싱 오류가 사용자에게 그대로 노출되므로 텍스트로 받아 직접 해석한다.
 */
export async function parseApiResponse<T>(res: Response): Promise<T> {
  const text = res.status === 204 ? '' : await res.text()

  if (text.trim() === '') {
    if (res.ok) return undefined as T
    throw apiError(`요청에 실패했습니다 (HTTP ${res.status})`, res.status)
  }

  let json: ApiResponse<T>
  try {
    json = JSON.parse(text) as ApiResponse<T>
  } catch {
    // JSON이 아닌 본문(프록시/게이트웨이 HTML 등) — 상태코드로 안내
    throw apiError(
      res.ok ? '서버 응답을 해석할 수 없습니다' : `요청에 실패했습니다 (HTTP ${res.status})`,
      res.status,
    )
  }

  if (json.success === false) {
    throw apiError(json.error?.message ?? 'Unknown error', res.status, json.error?.code, json.error?.details)
  }
  if (!res.ok) {
    throw apiError(`요청에 실패했습니다 (HTTP ${res.status})`, res.status)
  }
  return rewriteImageUrls(json.data as T)
}

function apiError(message: string, status: number, code?: string, details?: unknown): Error & { status?: number; code?: string; details?: unknown } {
  const err = new Error(message) as Error & { status?: number; code?: string; details?: unknown }
  err.status = status
  err.code = code
  err.details = details
  return err
}

/** apiFetch가 던진 에러에서 HTTP 상태 코드를 추출. 비-API 에러면 undefined */
export function apiErrorStatus(err: unknown): number | undefined {
  return (err as { status?: number })?.status
}
